import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from 'layout/Layout';
import { useGetAllWorkshopsQuery, useDeleteWorkshopMutation } from 'api/workshopApi';
import Swal from 'sweetalert2';
import { mediaUrl } from 'utils/mediaUrl';
import './Workshop.css';

const Workshop = () => {
  const { data: workshopsData, isLoading, isError } = useGetAllWorkshopsQuery();
  const [deleteWorkshop] = useDeleteWorkshopMutation();
  const navigate = useNavigate();

  const workshops = Array.isArray(workshopsData)
    ? workshopsData
    : workshopsData?.data || [];

  const handleDelete = async (slug) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This action will permanently delete the workshop.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        await deleteWorkshop(slug).unwrap();
        Swal.fire({
          title: 'Deleted!',
          text: 'Workshop deleted successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error('Delete error:', err);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete workshop.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#d33',
        });
      }
    }
  };

  const handleUpdateRedirect = (slug) => navigate(`/workshops/edit/${slug}`);
  const handleDetailsRedirect = (slug) => navigate(`/workshopDetails/${slug}`);

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const d = new Date();
    d.setHours(parseInt(hours, 10));
    d.setMinutes(parseInt(minutes, 10));
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getImageUrl = (path) => {
    if (!path) return 'https://via.placeholder.com/400x200?text=No+Image';
    return mediaUrl(path) || 'https://via.placeholder.com/400x200?text=No+Image';
  };

  const formatPrice = (price) => {
    const num = Number(price);
    return isNaN(num) ? price : num.toLocaleString('en-IN');
  };

  return (
    <Layout title="Manage Workshops" tagline="Organize your workshops.">
      <div className="ws-container">
        <div className="ws-header">
          <h2 className="ws-title">Create the Workshops</h2>
          <button
            className="ws-btn-primary"
            onClick={() => navigate(`/create workshop`)}
          >
            Create Workshop
          </button>
        </div>

        {isLoading && <p style={{ textAlign: 'center', marginTop: '2rem' }}>Loading workshops…</p>}
        {isError && <p style={{ textAlign: 'center', color: 'var(--danger-color)', marginTop: '2rem' }}>Error loading workshops.</p>}

        <div className="ws-grid">
          {workshops.map((workshop) => (
            <div key={workshop.slug} className="ws-card">
              <div className="ws-card-image-wrap">
                <img
                  src={getImageUrl(workshop.cover_photo)}
                  alt={workshop.title}
                  className="ws-card-image"
                  onError={(e) => { 
                    e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22400%22%20height%3D%22200%22%20viewBox%3D%220%200%20400%20200%22%3E%3Crect%20fill%3D%22%23ddd%22%20width%3D%22400%22%20height%3D%22200%22%2F%3E%3Ctext%20fill%3D%22%23555%22%20font-family%3D%22sans-serif%22%20font-size%3D%2224%22%20dy%3D%2210.5%22%20font-weight%3D%22bold%22%20x%3D%2250%25%22%20y%3D%2250%25%22%20text-anchor%3D%22middle%22%3ENo%20Image%3C%2Ftext%3E%3C%2Fsvg%3E';
                  }}
                />
                <div className="ws-card-overlay"></div>
              </div>

              <div className="ws-card-content">
                <h2 className="ws-card-title" title={workshop.title}>
                  {workshop.title}
                </h2>
                <p className="ws-card-subtitle">{workshop.subtitle}</p>

                <div className="ws-card-details">
                  <div className="ws-detail-item">
                    <span role="img" aria-label="Date" className="ws-detail-icon">📅</span>
                    <span>{new Date(workshop.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                  <div className="ws-detail-item">
                    <span role="img" aria-label="Time" className="ws-detail-icon">🕒</span>
                    <span>{formatTime(workshop.start_time)} – {formatTime(workshop.end_time)} {workshop.duration && `(${workshop.duration})`}</span>
                  </div>
                  <div className="ws-detail-item">
                    <span role="img" aria-label="Fees" className="ws-detail-icon">💸</span>
                    <span className="ws-price-original" style={{ textDecoration: 'line-through' }}>₹{formatPrice(workshop.course_fee_original)}</span>
                    <span className="ws-price-discounted">₹{formatPrice(workshop.course_fee_discounted)}</span>
                  </div>
                  <div className="ws-detail-item">
                    <span role="img" aria-label="Location" className="ws-detail-icon">🌐</span>
                    <span>{workshop.location}</span>
                  </div>
                  {workshop.languages && workshop.languages.length > 0 && (
                    <div className="ws-detail-item">
                      <span role="img" aria-label="Languages" className="ws-detail-icon">🗣️</span>
                      <span>{Array.isArray(workshop.languages) ? workshop.languages.join(', ') : workshop.languages}</span>
                    </div>
                  )}
                </div>

                <div className="ws-card-actions">
                  <button
                    onClick={() => handleUpdateRedirect(workshop.slug)}
                    className="ws-btn-action ws-btn-update"
                    aria-label={`Update ${workshop.title}`}
                  >
                    Update
                  </button>
                  <button
                    onClick={() => handleDelete(workshop.slug)}
                    className="ws-btn-action ws-btn-delete"
                    aria-label={`Delete ${workshop.title}`}
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => handleDetailsRedirect(workshop.slug)}
                    className="ws-btn-action ws-btn-details"
                    aria-label={`Details of ${workshop.title}`}
                  >
                    Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default Workshop;
